import React, {Fragment, useEffect, useState} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormDialog from "components/common/FormDialog";
import {ACTION_STATES} from "core/actions";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Button, Grid2, Typography } from "@mui/material";
import {validMultipleEmailRegex, validPhoneNumberRegex, validEmailRegex} from "core/validation";
import {Formik} from "formik";
import PropTypes from "prop-types";
import randomColor from "randomcolor";
import {connect} from "react-redux";
import * as yup from "yup";
import ConfirmDialog from "../../../common/ConfirmDialog";
import {actions, initState} from "../../logic/contactActions";
import {infoSection} from "../common/styles";
import ContactForm from "./ContactForm";
import {contactSection} from "./style";

const Form = ({ initialValues, disabled, submitHandlerRef, handleSubmit, contactFormCopyMode }) => {
    const validationScheme = yup.object().shape({
        firstName: yup.string().max(50, "Too Long.").required("Required"),
        lastName: yup.string().max(50, "Too Long.").required("Required"),
        title: yup.string().max(76, "Too Long."),
        addressList: yup.array().of(
            yup.object().shape({
                phone1: yup.string().matches(validPhoneNumberRegex, "Invalid phone number.").nullable(),
                phone2: yup.string().matches(validPhoneNumberRegex, "Invalid phone number.").nullable(),
                fax: yup.string().matches(validPhoneNumberRegex, "Invalid phone number.").nullable(),
                email: yup.string().matches(validMultipleEmailRegex, "Invalid email.").max(255, "255 Characters max.").nullable(),
            })
        ),
        assistantPhone: yup.string().matches(validPhoneNumberRegex, "Invalid phone number.").nullable(),
        assistantEmail: yup.string().matches(validEmailRegex, "Invalid email.").max(255, "255 Characters max.").nullable()
    });

    return (
        <Formik
            enableReinitialize
            initialValues={{ ...initialValues }}
            onSubmit={handleSubmit}
            validationSchema={validationScheme}
        >
            {(props) => (
                <ContactForm
                    {...props}
                    disabled={disabled}
                    contactFormCopyMode={contactFormCopyMode}
                    ref={submitHandlerRef}
                />
            )}
        </Formik>
    );
};
function HeaderContact({ contact }) {
    const isContactSelected = contact && contact.id;
    const contactInfo = isContactSelected ? contact.title : null;
    let avatarName = null;
    if (isContactSelected) {
        avatarName = contact.firstName ? contact.firstName[0] : "" + contact.lastName ? contact.lastName[0] : "";
    }
    const backgroundColor = React.useMemo(() => {
        return randomColor();
    }, [isContactSelected]);
    return (
        <div style={{ width: "100%" }}>
            {contact && contact.id ? (
                <span>
                    {avatarName && (
                        <Avatar
                            style={{
                                width: 30,
                                height: 30,
                                fontSize: 16,
                                backgroundColor,
                                marginRight: 10,
                                float: "left",
                            }}
                        >
                            {avatarName}
                        </Avatar>
                    )}
                    <span style={{ float: "left" }}>
                        <Typography variant={"subtitle1"}>{contact.firstName + " " + contact.lastName}</Typography>
                        {contactInfo && <Typography variant={"body2"}>{contactInfo}</Typography>}
                    </span>
                </span>
            ) : (
                <Typography variant={"subtitle1"}>Select a contact</Typography>
            )}
        </div>
    );
}

HeaderContact.propTypes = {
    contact: PropTypes.object,
};
const ContactSection = (props) => {
    const contactSectionClasses = contactSection();
    const infoSectionClasses = infoSection();

    const {
        contact,
        company,
        saveContact,
        removeContact,
        savingContactStatus,
        clearStatus,
        isContactFormOpen,
        contactFormEditMode,
        contactFormCopyMode,
        copiedContact,
        openContactForm,
        copyContact,
        closeForm,
        selectCompany,
    } = props;

    const contactId = contact && contact.id ? contact.id : null;
    const hasUser = contact && contact.id;
    const [expanded, setExpanded] = useState(hasUser);
    const [contactIdToDelete, setContactIdToDelete] = useState(null);

    let saved = ACTION_STATES.SUCCESS === savingContactStatus;

    useEffect(() => {
        if (saved) handleClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savingContactStatus]);

    useEffect(() => {
        if (contactId) {
            setExpanded(true);
        }
    }, [contactId]);

    function handleClose() {
        closeForm();
        clearStatus();
    }

    function handleSubmit(values) {
        const newContact = {
            id: values.id,
            companyId: (values.company && values.company.value) || company.id,
            advertisementTypesIds: values.advTypes ? values.advTypes.map((adv) => adv.id) : [],
            contactRolesIds: values.contactRoles ? values.contactRoles.map((role) => role.id) : [],
            firstName: values.firstName ? values.firstName : "",
            lastName: values.lastName ? values.lastName : "",
            title: values.title ? values.title : "",
            addresses: values.addressList ? values.addressList : [],
            assistant: values.assistant ? values.assistant : "",
            assistantPhone: values.assistantPhone ? values.assistantPhone : "",
            assistantEmail: values.assistantEmail ? values.assistantEmail : "",
            note: values.note ? values.note : "",
            pronoun: values?.pronoun ? values?.pronoun : ""
        };

        saveContact(newContact);
        if (contactFormCopyMode && values.company) {
            selectCompany(values.company);
        }
    }

    function openInEditMode() {
        openContactForm(true);
    }

    function openInCopyMode() {
        copyContact(contact);
        openContactForm(false);
    }

    function handleContactDelete() {
        const id = contact && contact.id ? contact.id : null;
        setContactIdToDelete(id);
    }
    function handleDeleteConfirmation() {
        removeContact(contactIdToDelete);
        setContactIdToDelete(null);
    }
    function handleDeleteCancel() {
        setContactIdToDelete(null);
    }

    return (
        (<Fragment>
            <FormDialog
                handleClose={handleClose}
                openForm={isContactFormOpen}
                formText="Contact"
                editMode={contactFormEditMode}
                formComponent={
                    <Form
                        initialValues={
                            contactFormCopyMode && copiedContact
                                ? { ...copiedContact, company: { ...company, value: company.id } }
                                : contactFormEditMode
                                    ? contact
                                    : initState.selectedContact
                        }
                        handleSubmit={handleSubmit}
                        contactFormCopyMode={contactFormCopyMode}
                    />
                }
            />
            {contactIdToDelete && (
                <ConfirmDialog
                    open
                    message="Would you like to delete this user?"
                    btnConfirm={
                        <Button
                            onClick={handleDeleteConfirmation}
                            className={contactSectionClasses.deleteButtonConfirm}
                        >
                            {"Delete"}
                        </Button>
                    }
                    onCancel={handleDeleteCancel}
                />
            )}
            <Accordion expanded={expanded} className={infoSectionClasses.expansionPanel}>
                <AccordionSummary
                    onClick={() => setExpanded(!expanded)}
                    expandIcon={<ExpandMoreIcon />}
                    className={infoSectionClasses.expansionPanelSummay}
                >
                    <HeaderContact contact={contact} />
                </AccordionSummary>
                <AccordionDetails className={infoSectionClasses.expansionPanelDetails}>
                    {contact && contact.id ? (
                        <Grid2 container>
                            <Grid2 size={12}>
                                <Form
                                    initialValues={contact && contact.id ? contact : initState.selectedContact}
                                    disabled
                                />
                            </Grid2>
                            <Grid2 className={infoSectionClasses.buttonsContainer} size={12}>
                                {}
                                <Button
                                    variant="outlined"
                                    disabled={company !== initState.selectedCompany ? false : true}
                                    color="default"
                                    onClick={openInEditMode}
                                    className={contactSectionClasses.editButton}
                                >
                                    Edit Contact
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={company !== initState.selectedCompany ? false : true}
                                    color="default"
                                    onClick={openInCopyMode}
                                    className={contactSectionClasses.editButton}
                                >
                                    Copy Contact
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={company !== initState.selectedCompany ? false : true}
                                    onClick={handleContactDelete}
                                    className={contactSectionClasses.deleteButton}
                                >
                                    Delete Contact
                                </Button>
                            </Grid2>
                        </Grid2>
                    ) : (
                        <Typography paragraph variant={"h6"}>
                            Please select contact from the list
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        </Fragment>)
    );
};

ContactSection.propTypes = {
    contact: PropTypes.object,
    company: PropTypes.object,
    saveContact: PropTypes.func.isRequired,
    removeContact: PropTypes.func.isRequired,
    savingContactStatus: PropTypes.string,
    clearStatus: PropTypes.func.isRequired,
    isContactFormOpen: PropTypes.bool.isRequired,
    contactFormEditMode: PropTypes.bool.isRequired,
    contactFormCopyMode: PropTypes.bool.isRequired,
    copiedContact: PropTypes.object,
    openContactForm: PropTypes.func.isRequired,
    copyContact: PropTypes.func.isRequired,
    closeForm: PropTypes.func.isRequired,
    selectCompany: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    contact: state.contacts.selectedContact,
    company: state.contacts.selectedCompany,
    savingContactStatus: state.contacts.savingContactStatus,
    isContactFormOpen: state.contacts.openContactForm,
    contactFormEditMode: state.contacts.contactEditMode,
    contactFormCopyMode: state.contacts.contactCopyMode,
    copiedContact: state.contacts.copiedContact,
});

const mapDispatchToProps = (dispatch) => ({
    saveContact: (contact) => dispatch(actions.SAVE_CONTACT.send(contact)),
    copyContact: (contact) => dispatch(actions.COPY_CONTACT.call(contact)),
    removeContact: (id) => dispatch(actions.REMOVE_CONTACT.send(id)),
    openContactForm: (editMode) => dispatch(actions.OPEN_CONTACT_FORM.call(editMode)),
    clearStatus: () => dispatch(actions.CLEAR_STATUS.call()),
    closeForm: () => dispatch(actions.CLOSE_FORM.call()),
    selectCompany: (company) => dispatch(actions.SELECT_COMPANY.call(company)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContactSection);
